import { Component, Inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';

import { AbstractSubscriptionManager } from '@app/shared/classes/subscription-manager';
import { ButtonColors } from '../button/button.constants';
import { ModalService } from '@app/shared/services/modal.service';
import { ModalData, ModalResponse } from '@app/shared/models/modal-data';
import { TimesheetDOS } from '@app/timesheet-user/services/timesheet-dos.service';
import { ModalTimesheetDosService } from '@app/shared/services/modal-timesheet-dos.service';
import { CustomDeadlineDOS } from '@settings/components/custom-deadline/services/custom-deadline-dos.service';
import { ModalConfig } from '@app/shared/constants/modal.constants';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from '@app/services/message.service';
import { CustomNotificationsDOS } from '@app/settings/components/custom-notifications/services/custom-notifications-dos.service';
import { DefaultNotificationsDOS } from '@app/settings/components/default-notifications/services/default-notifications-dos.service';
import { MonthPickerService } from '@app/shared/components/month-picker/month-picker.service';
import { InformationModalComponent } from '../information-modal/information-modal.component';
import { NotificationType } from '@settings/models';

@Component({
  selector: 'app-confirm-modal',
  templateUrl: './confirm-modal.component.html',
  styleUrls: ['./confirm-modal.component.scss']
})
export class ConfirmModalComponent extends AbstractSubscriptionManager {
  approveText: string = this.modalData.approveText;
  cancelText: string = this.modalData.cancelText;
  userAccessChange: boolean;
  readonly primaryColor: ButtonColors = ButtonColors.Primary;
  readonly warnColor: ButtonColors = ButtonColors.Warn;
  readonly timesheetDate$: Observable<Date> = this._timesheetDOS.timesheetDate$;
  readonly selectedCustomDeadlineMonth$ = this._customDeadlineDOS.selectedMonth$;
  readonly selectedCustomNotificationsMonth$ = this._customNotificationsDOS.selectedMonth$;
  readonly customDeadlineModal = ModalConfig.CustomDeadline;
  readonly customNotificationsModal = ModalConfig.CustomNotifications;

  constructor(
    private _timesheetDOS: TimesheetDOS,
    private _modalService: ModalService,
    private _modalTimesheetDosService: ModalTimesheetDosService,
    private _dialogRef: MatDialogRef<ConfirmModalComponent>,
    private _messageService: MessageService,
    private _customDeadlineDOS: CustomDeadlineDOS,
    private _customNotificationsDOS: CustomNotificationsDOS,
    private _defaultNotificationsDOS: DefaultNotificationsDOS,
    private _matDialog: MatDialog,
    private _monthPickerService: MonthPickerService,
    @Inject(MAT_DIALOG_DATA) public modalData: ModalData
  ) {
    super();
  }

  modalAction(): void {
    this.subscription.add(
      this._modalService.handleModalAction(this.modalData).subscribe({
        next: () => this._modalActionSuccess(),
        error: (err) => this._modalActionError(err)
      })
    );
  }

  private _modalActionSuccess(): void {
    if (this.modalData.userStatus) {
      this._handleChangeAccessModal();
    } else if (this.modalData.name === ModalConfig.CustomDeadline) {
      this._customDeadlineConfirmSuccess();
    } else if (this.modalData.name === ModalConfig.CustomNotifications) {
      this._handleCustomNotificationsModalAction();
    } else if (this.modalData.name === ModalConfig.EmptyDefaultNotifications) {
      this._handleEmptyDefaultNotifications();
    } else if (this.modalData.name === ModalConfig.UnsavedTimesheet) {
      this._handleUnsavedTimesheet();
      return null;
    } else {
      this._dialogRef.close();
    }
    this._messageService.showSuccessMessage(this.modalData.successMessage);
  }

  private _modalActionError(error: HttpErrorResponse): Observable<never> {
    this._dialogRef.close();
    this._messageService.showErrorMessage(this.modalData.errorMessage);
    return throwError(error);
  }

  closeModal(): void {
    if (this.modalData.userStatus) {
      this._passDataOnModalClose();
    } else if (this.modalData.name === ModalConfig.CustomDeadline) {
      this._discardModifiedDeadlineData(this.modalData.selectedMonth);
    } else if (this.modalData.name === ModalConfig.CustomNotifications) {
      this._handleCustomNotificationsModalAction();
    } else if (this.modalData.name === ModalConfig.UnsavedTimesheet) {
      this._closeUnsavedTimesheet(this.modalData.timesheetData);
    } else {
      this._dialogRef.close();
    }
  }

  private _handleChangeAccessModal(): void {
    this.userAccessChange = this._modalTimesheetDosService.isAccessChangeAccepted();
    this._passDataOnModalClose();
  }

  private _passDataOnModalClose(): void {
    this._dialogRef.close({
      userStatus: this.modalData.userStatus,
      userAccessChange: this.userAccessChange,
      username: this.modalData.username
    } as ModalResponse);
  }

  private _discardModifiedDeadlineData(newMonth: Date): void {
    this._customDeadlineDOS.updateSelectedMonth(newMonth);
    this._dialogRef.close();
    this._customDeadlineDOS.updateUnsavedCustomDeadline(false);
  }

  private _customDeadlineConfirmSuccess(): void {
    this._customDeadlineDOS.updateSelectedMonth(this.modalData.selectedMonth);
    this._customDeadlineDOS.updateUnsavedCustomDeadline(false);

    this._dialogRef.close();
  }

  private _handleCustomNotificationsModalAction(): void {
    this._customNotificationsDOS.updateSelectedMonth(this.modalData.payload.nextMonth);
    this._customNotificationsDOS.updateDataModified(false);
    this._customNotificationsDOS.updateDataRestored(false);

    this._dialogRef.close();
  }

  private _handleEmptyDefaultNotifications(): void {
    this._defaultNotificationsDOS.updateDefaultNotificationMessages([
      { subject: '', text: '', notificationType: NotificationType.DEADLINE },
      { subject: '', text: '', notificationType: NotificationType.OVERVIEW }
    ]);

    this._dialogRef.close();
  }

  private _handleUnsavedTimesheet(): void {
    const isTimesheetValid = !this._timesheetDOS.getValidationErrorsSnapshot();

    if (!isTimesheetValid) {
      const modalConfig = this._modalService.createValidationModalData();
      this._matDialog.open(InformationModalComponent, modalConfig);
    }
    this._dialogRef.close();
  }

  private _closeUnsavedTimesheet({ parentComponent, currentMonth, actionType, selectedDate }): void {
    this._monthPickerService.setNewMonth(parentComponent, currentMonth, actionType, selectedDate);
    this._timesheetDOS.updateUserTimesheetChangedStatus(false);
    this._dialogRef.close();
  }
}
